import localStorageEffect from '@/utils/recoil/localStorageEffect';
import { atom, atomFamily, selector } from 'recoil';

import { ChartHoverState } from './QueryChart/Tooltip/types';
import {
  Chart,
  ChartConfig,
  RelativeTimeRange,
  ResponseTimeMode,
  SelectedMetricsUnits,
  SharedEndpointChart,
  TimeRange,
  TrainingJobChart,
} from './types';
import { getAbsoluteTimeRangeFromRelative } from './utils';

export const relativeTimeRangeState = atom<RelativeTimeRange>({
  default: RelativeTimeRange.LAST_1_HOUR,
  key: 'metrics-relative-time-range',
});

export const timeRangeState = atom<TimeRange>({
  default: getAbsoluteTimeRangeFromRelative(RelativeTimeRange.LAST_1_HOUR),
  key: 'metrics-absolute-time-range',
});

export const chartHoverState = atom<ChartHoverState>({
  default: undefined,
  key: 'chart-hover-state',
});

/**
 * Tracks the number of metric queries currently loading data for a new relative time range.
 * This doesn't include the initial load.
 */
export const loadingNewRelativeTimeRangeCountState = atom<number>({
  default: 0,
  key: 'loading-new-relative-time-range-count',
});

export const loadingNewRelativeTimeRangeSelector = selector<boolean>({
  get: ({ get }) => get(loadingNewRelativeTimeRangeCountState) > 0,
  key: 'loading-new-relative-time-range-bool',
});

export const responseTimeModeState = atom<ResponseTimeMode>({
  default: ResponseTimeMode.EndToEndLatency,
  key: 'e2e-response-time-mode',
  effects: [localStorageEffect('e2e-response-time-mode')],
});

const DEFAULT_CHART_CONFIG: ChartConfig[] = [
  ...Object.values(Chart).map((chart) => ({
    id: chart,
    hidden: [Chart.AsyncQueueSize, Chart.TimeInAsyncQueue].includes(chart),
  })),
];

const DEFAULT_SHARED_ENDPOINT_CHART_CONFIG: ChartConfig[] = [
  ...Object.values(SharedEndpointChart).map((chart) => ({
    id: chart,
    hidden: false,
  })),
];

const DEFAULT_TRAINING_JOB_CHART_CONFIG: ChartConfig[] = [
  ...Object.values(TrainingJobChart).map((chart) => ({
    id: chart,
    hidden: false,
  })),
];

export const sharedEndpointMetricsConfigState = atomFamily<ChartConfig[], string>({
  default: DEFAULT_SHARED_ENDPOINT_CHART_CONFIG,
  key: 'shared-endpoint-metrics-config',
  effects: (key) => [localStorageEffect(`shared-endpoint-metrics-config-${key}`)],
});

export const trainingJobMetricsConfigState = atomFamily<ChartConfig[], string>({
  default: DEFAULT_TRAINING_JOB_CHART_CONFIG,
  key: 'training-job-metrics-config',
  effects: (key) => [localStorageEffect(`training-job-metrics-config-${key}`)],
});

export const metricsConfigState = atomFamily<ChartConfig[], string>({
  default: DEFAULT_CHART_CONFIG,
  key: 'metrics-config',
  effects: (key) => [localStorageEffect(`metrics-config-${key}`)],
});

export const selectedMetricsUnitsState = atomFamily<SelectedMetricsUnits, string>({
  default: {
    // NOTE: this contradicts the idea of minimal state, but subset of rendered legends is not available in the header
    // so, to avoid mixing up the states, we have to keep it separate
    header: [],
    legends: [],
    hoveredLegends: [],
  },
  key: 'selected-metrics-units',
});
